import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo, PaginatedResponse } from "./baseApi";
import { Endgood } from "./endgoodApi";
import { BomHeader } from "./bomHeaderApi";
import { SalesOrder } from "./salesOrderApi";

// Enums for WorkOrder
export enum WorkOrderPriority {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum WorkOrderStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

// Main WorkOrder interface - Updated with flattened fields
export interface WorkOrder {
  id: number;
  workOrderNumber: string;
  customerName: string;
  quantity: number;
  completedQuantity: number;
  remainingQuantity: number;
  priority: WorkOrderPriority;
  status: WorkOrderStatus;
  startDate?: string;
  dueDate?: string;
  assignedTo?: string;
  department?: string;
  estimatedHours?: number;
  actualHours: number;
  notes?: string;
  isActive: boolean;
  branchId?: number;
  endgoodId?: number;
  salesOrderId?: number;
  bomHeaderId?: number;
  createdBy?: number;
  updatedBy?: number;
  createdAt: string;
  updatedAt: string;
  
  // Relationships
  endgood?: Endgood;
  salesOrder?: SalesOrder;
  bomHeader?: BomHeader;
  
  // Flattened fields from API response
  endgoodName?: string;
  endgoodSku?: string;
  salesOrderNumber?: string;
  bomNumber?: string;
  bomName?: string;
  completionPercentage?: number;
  isOverdue?: boolean;
  isCompleted?: boolean;
  isInProgress?: boolean;
}

export interface WorkOrderResponse extends PaginatedResponse<WorkOrder> {}

// DTOs for API operations
export interface WorkOrderCreateRequest {
  customerName: string;
  quantity: number;
  priority?: WorkOrderPriority;
  status?: WorkOrderStatus;
  startDate?: string;
  dueDate?: string;
  assignedTo?: string;
  department?: string;
  estimatedHours?: number;
  actualHours?: number;
  notes?: string;
  branchId?: number;
  endgoodId?: number;
  salesOrderId?: number;
  bomHeaderId?: number;
}

export interface WorkOrderUpdateRequest extends WorkOrderCreateRequest {
  id: number;
}

export interface WorkOrderFilters {
  status?: WorkOrderStatus;
  priority?: WorkOrderPriority;
  branchId?: number;
  customerId?: number;
  salesOrderId?: number;
  endgoodId?: number;
  assignedTo?: string;
  department?: string;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
  workOrderNumber?: string;
}

export interface WorkOrderStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  onHold: number;
  overdue: number;
  totalEstimatedHours: number;
  totalActualHours: number;
}

class WorkOrderApiService extends BaseApiService {

  // Get all work orders (simple list without pagination)
  async getWorkOrders(): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        API_ENDPOINTS.WORK_ORDERS.LIST
      );
      
      return response;
    } catch (error) {
      // console.error('WorkOrderApiService: Error fetching work orders:', error);
      toast.error('Failed to fetch work orders');
      throw error;
    }
  }

  /** Active work orders via GET /production/work-orders/all/optimized (lighter payload). */
  async getWorkOrdersOptimized(): Promise<ApiResponse<WorkOrder[]>> {
    try {
      return await this.makeRequest<ApiResponse<WorkOrder[]>>(
        API_ENDPOINTS.WORK_ORDERS.LIST_OPTIMIZED,
        { method: HTTP_METHODS.GET }
      );
    } catch (error) {
      toast.error('Failed to fetch work orders');
      throw error;
    }
  }

  /** Minimal WO list for a branch: GET /production/work-orders/all/minimal?branchId= */
  async getWorkOrdersMinimalByBranch(
    branchId: number,
    page: number = 0,
    size: number = 500
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        branchId: String(branchId),
        page: String(page),
        size: String(size),
      });
      const response = await this.makeRequest<ApiResponse<any>>(
        `${API_ENDPOINTS.WORK_ORDERS.LIST}?${params.toString()}`,
        { method: HTTP_METHODS.GET }
      );
      if (response.success && response.data) {
        const content = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data.content)
            ? response.data.content
            : [];
        return { ...response, data: content as WorkOrder[] };
      }
      return { ...response, data: [] };
    } catch (error) {
      toast.error('Failed to fetch work orders');
      throw error;
    }
  }

  // Get work orders with pagination and filters (optional method)
  async getWorkOrdersWithPagination(
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderFilters
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to query parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.LIST}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      // console.error('WorkOrderApiService: Error fetching work orders:', error);
      toast.error('Failed to fetch work orders');
      throw error;
    }
  }

  // Get work order by ID
  async getWorkOrderById(id: string): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.DETAIL(id)
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order details');
      throw error;
    }
  }

  // Create new work order
  async createWorkOrder(workOrderData: WorkOrderCreateRequest): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.CREATE, 
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(workOrderData),
        }
      );
      toast.success('Work order created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create work order');
      throw error;
    }
  }

  // Update work order
  async updateWorkOrder(id: string, workOrderData: WorkOrderUpdateRequest): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.UPDATE(id), 
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(workOrderData),
        }
      );
      toast.success('Work order updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update work order');
      throw error;
    }
  }

  // Delete work order
  async deleteWorkOrder(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.WORK_ORDERS.DELETE(id), 
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Work order deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete work order');
      throw error;
    }
  }

  // Search work orders with pagination
  async searchWorkOrders(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderFilters
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters to search parameters
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
      }

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.SEARCH}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to search work orders');
      throw error;
    }
  }

  // Get work orders by status
  async getWorkOrdersByStatus(
    status: WorkOrderStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.BY_STATUS(status)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error(`Failed to fetch ${status.toLowerCase()} work orders`);
      throw error;
    }
  }

  // Get work orders by priority
  async getWorkOrdersByPriority(
    priority: WorkOrderPriority, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.BY_PRIORITY(priority)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error(`Failed to fetch ${priority.toLowerCase()} priority work orders`);
      throw error;
    }
  }

  // Get work orders by branch
  async getWorkOrdersByBranch(
    branchId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.BY_BRANCH(branchId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch branch work orders');
      throw error;
    }
  }

  // Get work orders by customer
  async getWorkOrdersByCustomer(
    customerId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.BY_CUSTOMER(customerId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch customer work orders');
      throw error;
    }
  }

  // Get work orders by sales order
  async getWorkOrdersBySalesOrder(
    salesOrderId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.BY_SALES_ORDER(salesOrderId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch sales order work orders');
      throw error;
    }
  }

  // Get work orders by endgood
  async getWorkOrdersByEndgood(
    endgoodId: string, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<ApiResponse<WorkOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await this.makeRequest<ApiResponse<WorkOrder[]>>(
        `${API_ENDPOINTS.WORK_ORDERS.BY_ENDGOOD(endgoodId)}?${params.toString()}`
      );
      
      return response;
    } catch (error) {
      toast.error('Failed to fetch endgood work orders');
      throw error;
    }
  }

  // Update work order status
  async updateWorkOrderStatus(id: string, status: WorkOrderStatus): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.UPDATE_STATUS(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ status }),
        }
      );
      toast.success(`Work order status updated to ${status.toLowerCase()}`);
      return response;
    } catch (error) {
      toast.error('Failed to update work order status');
      throw error;
    }
  }

  // Update work order priority
  async updateWorkOrderPriority(id: string, priority: WorkOrderPriority): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.UPDATE_PRIORITY(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ priority }),
        }
      );
      toast.success(`Work order priority updated to ${priority.toLowerCase()}`);
      return response;
    } catch (error) {
      toast.error('Failed to update work order priority');
      throw error;
    }
  }

  // Assign work order to user/department
  async assignWorkOrder(id: string, assignedTo: string, department?: string): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.ASSIGN(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ assignedTo, department }),
        }
      );
      toast.success('Work order assigned successfully');
      return response;
    } catch (error) {
      toast.error('Failed to assign work order');
      throw error;
    }
  }

  // Complete work order
  async completeWorkOrder(id: string, actualHours?: number, notes?: string): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.COMPLETE(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ actualHours, notes }),
        }
      );
      toast.success('Work order completed successfully');
      return response;
    } catch (error) {
      toast.error('Failed to complete work order');
      throw error;
    }
  }

  // Cancel work order
  async cancelWorkOrder(id: string, notes?: string): Promise<ApiResponse<WorkOrder>> {
    try {
      const response = await this.makeRequest<ApiResponse<WorkOrder>>(
        API_ENDPOINTS.WORK_ORDERS.CANCEL(id), 
        {
          method: HTTP_METHODS.PATCH,
          body: JSON.stringify({ notes }),
        }
      );
      toast.success('Work order cancelled successfully');
      return response;
    } catch (error) {
      toast.error('Failed to cancel work order');
      throw error;
    }
  }

  // Get work order statistics
  async getWorkOrderStats(filters?: WorkOrderFilters): Promise<ApiResponse<WorkOrderStats>> {
    try {
      let endpoint = API_ENDPOINTS.WORK_ORDERS.GET_STATS;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      }

      const response = await this.makeRequest<ApiResponse<WorkOrderStats>>(endpoint);
      return response;
    } catch (error) {
      toast.error('Failed to fetch work order statistics');
      throw error;
    }
  }

  // Export work orders
  async exportWorkOrders(filters?: WorkOrderFilters, format: 'csv' | 'excel' = 'csv'): Promise<Blob> {
    try {
      let endpoint = API_ENDPOINTS.WORK_ORDERS.EXPORT;
      
      if (filters) {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, value.toString());
          }
        });
        params.append('format', format);
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
      } else {
        endpoint += `?format=${format}`;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081/api/v1'}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      toast.error('Failed to export work orders');
      throw error;
    }
  }

  // Helper method to extract data from API response
  extractData<T>(response: ApiResponse<T>): T {
    if (!response.success) {
      throw new Error(response.message || 'API request failed');
    }
    return response.data;
  }

  // Helper method to get work orders with extracted data
  async getWorkOrdersData(
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderFilters
  ): Promise<{ workOrders: WorkOrder[], pagination: PaginationInfo }> {
          const response = await this.getWorkOrdersWithPagination(page, pageSize, filters);
    const workOrders = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrders.length,
      pageSize: pageSize
    };
    
    return {
      workOrders,
      pagination
    };
  }

  // Helper method to search work orders with extracted data
  async searchWorkOrdersData(
    query: string, 
    page: number = 1, 
    pageSize: number = 10,
    filters?: WorkOrderFilters
  ): Promise<{ workOrders: WorkOrder[], pagination: PaginationInfo }> {
    const response = await this.searchWorkOrders(query, page, pageSize, filters);
    const workOrders = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrders.length,
      pageSize: pageSize
    };
    
    return {
      workOrders,
      pagination
    };
  }

  // Helper method to get work orders by status with extracted data
  async getWorkOrdersByStatusData(
    status: WorkOrderStatus, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<{ workOrders: WorkOrder[], pagination: PaginationInfo }> {
    const response = await this.getWorkOrdersByStatus(status, page, pageSize);
    const workOrders = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrders.length,
      pageSize: pageSize
    };
    
    return {
      workOrders,
      pagination
    };
  }

  // Helper method to get work orders by priority with extracted data
  async getWorkOrdersByPriorityData(
    priority: WorkOrderPriority, 
    page: number = 1, 
    pageSize: number = 10
  ): Promise<{ workOrders: WorkOrder[], pagination: PaginationInfo }> {
    const response = await this.getWorkOrdersByPriority(priority, page, pageSize);
    const workOrders = this.extractData(response);
    
    // Handle case where pagination is not present in response
    const pagination: PaginationInfo = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: workOrders.length,
      pageSize: pageSize
    };
    
    return {
      workOrders,
      pagination
    };
  }
}

export const workOrderApiService = new WorkOrderApiService();
